import {Clone, GetEntries, GetErrorMessagesUnderElement, CloneWithPrototypes, E} from "js-vextensions";
import Moment from "moment";
import {Column, Pre, RowLR, Select, Spinner, TextInput, Text, Row, Button} from "react-vcomponents";
import {BaseComponent, BaseComponentWithConnector, BaseComponentPlus} from "react-vextensions";
import {Sound, SoundType} from "../../../Server/DB/sounds/@Sound.js";
import {InfoButton, YoutubeSpeed, YoutubeQuality, GetVoices, Observer, observer_simple, RunInAction, useForceUpdate, RunInAction_Set} from "web-vcore";
import {name_general} from "../../../Utils/General/SharedPatterns.js";
import {User} from "../../../Server/DB/users/@User.js";
import {GenericEntryInfoUI} from "../../@Shared/CommonPropUIs/GenericEntryInfoUI.js";
import {BoxController, ShowMessageBox} from "react-vmessagebox";
import {AddSound} from "../../../Server/Commands/SoundCommands.js";
import {AddEntity} from "../../../Server/Commands/EntityCommands.js";
import {Entity, EntryVisibility, SimpleVisibility_AsVisibleToGroup, VisibleToGroups_AsSimpleVisibility} from "../../../Server/DB/entities/@Entity.js";
import {TagsInput} from "../../../Utils/ReactComponents/TagsInput.js";
import {GetUser, MeID} from "../../../Server/DB/users.js";
import {PreviewImageSquare} from "../../@Shared/PreviewImageSquare.js";
import {DialogStyle} from "../../../Utils/UI/GlobalStyles.js";
import {store} from "../../../Store/index.js";
import {useEffect, useRef, useState} from "react";
import React from "react";
import {GenerateUUID, observer_mgl} from "mobx-graphlink";
import {DetailsUI_Base_Props, useDetailsUICore} from "../../@Shared/DetailsUI_Base.js";
import {EntityPool} from "../../../Store/main/content.js";

export function ShowAddEntityDialog(pool: EntityPool, initialData?: Partial<Entity>, postAdd?: (data: Entity, id: string)=>any) {
	const uiState = store.main.content.entities;

	let newEntry = new Entity(E({
		name: "",
		//visibleToGroups: [MeID(), "public"],
		visibleToGroups: SimpleVisibility_AsVisibleToGroup(uiState.lastEntityVisibility, MeID()!),
	}, initialData));
	const getCommand_forOnlineAdd = ()=>new AddEntity({entry: newEntry});
	const doActualAdd = async()=>{
		if (pool == EntityPool.online) {
			return await getCommand_forOnlineAdd().RunOnServer();
		} else if (pool == EntityPool.local) {
			const id = GenerateUUID();
			newEntry._isLocal = true;
			newEntry.id = id;
			const meID = MeID();
			if (meID == null) throw new Error("Cannot add local entity without being signed in. (all entries must still have a 'creator' field)");
			newEntry.creator = meID;
			newEntry.createdAt = Date.now();
			RunInAction("ShowAddEntityDialog.doActualAdd", ()=>{
				store.main.content.entities.entities_local.push(newEntry);
			});
			return {id};
		}
		throw new Error(`Unknown entity pool: ${pool}`);
	};

	const boxController: BoxController = ShowMessageBox({
		title: "Add entity", cancelButton: true,
		message: observer_simple(()=>{
			const tempCommand = getCommand_forOnlineAdd();
			boxController.UpdateOptions({okButtonProps: {
				enabled: tempCommand.Validate_Safe() == null,
				title: tempCommand.validateError,
			}});
			return (
				<Column style={DialogStyle({width: 600})}>
					<EntityDetailsUI baseData={newEntry} phase="create"
						onChange={(val, error)=>{
							newEntry = val;
							boxController.UpdateUI();
						}}/>
				</Column>
			);
		}),
		onOK: async()=>{
			RunInAction("ShowAddEntityDialog.onOK", ()=>{
				uiState.lastEntityVisibility = VisibleToGroups_AsSimpleVisibility(newEntry.visibleToGroups);
			});
			const {id} = await doActualAdd();
			if (postAdd) postAdd(newEntry, id);
		},
	});
}

const splitAt = 120, width = "100%";

type Props = DetailsUI_Base_Props<Entity>;
export const EntityDetailsUI = observer_mgl((props: Props)=>{
	const {phase, style} = props;
	const {rootRef, newData, helpers} = useDetailsUICore(props);
	const {Change, creating, enabled} = helpers;
	const [previewing, setPreviewing] = useState(false);

	useEffect(()=>{
		const listener = (e: ClipboardEvent)=>{
			const items = (e.clipboardData ?? e["originalEvent"]?.clipboardData ?? {}).items ?? [];
			for (const item of items) {
				if (item.kind == "file") {
					const blob = item.getAsFile();
					const reader = new FileReader();
					reader.onload = event=>{
						const imageDataStr = event.target!.result as string;
						Change(a=>a.image_embedded = imageDataStr);
					};
					reader.readAsDataURL(blob);
				}
			}
		};
		document.addEventListener("paste", listener);
		return ()=>document.removeEventListener("paste", listener);
	}, [Change, newData]);

	/*const getValidationError = ()=>{
		return GetErrorMessagesUnderElement(rootRef.current)[0];
	};*/

	return (
		<Column ref={rootRef} style={style}>
			{phase != "create" &&
				<GenericEntryInfoUI id={newData.id} creatorID={newData.creator} createdAt={newData.createdAt} />}
			<RowLR mt={5} splitAt={splitAt} style={{width}}>
				<Text>Name:</Text>
				<TextInput pattern={name_general} required
					enabled={enabled} style={{width: "100%"}}
					value={newData.name} onChange={val=>Change(a=>a.name = val)} />
			</RowLR>
			<RowLR mt={5} splitAt={splitAt} style={{width}}>
				<Text>Visibility:</Text>
				<Select options={GetEntries(EntryVisibility)} enabled={enabled}
					value={VisibleToGroups_AsSimpleVisibility(newData.visibleToGroups)}
					onChange={val=>Change(a=>a.visibleToGroups = SimpleVisibility_AsVisibleToGroup(val, MeID()!))} />
			</RowLR>
			<RowLR mt={5} splitAt={splitAt} style={{width}}>
				<Text>Suggested tags:</Text>
				<TagsInput enabled={enabled} style={{width: "100%"}} value={newData.tags} onChange={val=>Change(a=>a.tags = val)} />
			</RowLR>
			<RowLR mt={5} splitAt={splitAt} style={{width}}>
				<Text>URL:</Text>
				<TextInput
					enabled={enabled} style={{width: "100%"}}
					value={newData.url} onChange={val=>Change(a=>a.url = val)} />
			</RowLR>
			<RowLR mt={5} splitAt={splitAt} style={{width}}>
				<Text>Image (embedded):</Text>
				<TextInput
					enabled={enabled} style={{width: "100%"}}
					value={newData.image_embedded} onChange={val=>Change(a=>a.image_embedded = val)} />
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<Text>Square:</Text>
				<Row style={{flexWrap: "wrap"}}>
					<Row>
						<Text>Center (x, y):</Text>
						<Spinner ml={5} enabled={enabled} min={0} max={100}
							value={(newData.square_center.x * 100).RoundTo(1)} onChange={val=>Change(a=>a.square_center.x = val / 100)} />
						<Spinner enabled={enabled} min={0} max={100}
							value={(newData.square_center.y * 100).RoundTo(1)} onChange={val=>Change(a=>a.square_center.y = val / 100)} />
					</Row>
					<Row>
						<Text>Size:</Text>
						<Spinner ml={5} enabled={enabled} min={1} max={100}
							value={(newData.square_size * 100).RoundTo(1)} onChange={val=>Change(a=>a.square_size = val / 100)} />
					</Row>
				</Row>
			</RowLR>
			<Button text={previewing ? "Hide preview" : "Preview entity"} mt={5} width={200} onClick={()=>{
				setPreviewing(!previewing);
			}} />
			{previewing &&
				<div style={{position: "relative"}}>
					<PreviewImageSquare entry={newData} style={{width: "100%", paddingBottom: "100%"}}/>
				</div>}
		</Column>
	);
});